import { UserInformation } from "@/api/signin";
import SignupHeader from "@/components/pages/signup/signupHeader";
import Spinner from "@/components/ui/spinner";
import { useToast } from "@/components/ui/use-toast";
import Typography from "@/components/ui/typography";
import { US_STATES_ID } from "@/constants/common-constants";
import { useAuth } from "@/context/auth-context";
import { userKeys } from "@/features/auth/keys";
import useCreateGuestBusinessDetails from "@/features/guest-business-details/useCreateGuestBusinessDetails";
import useGetStates from "@/features/states/useGetStates";
import { queryClient } from "@/query-client";
import { useLoaderData, useRouter } from "@tanstack/react-router";
import { BusinessDetailsForm } from "../Home/setting/business-details/BusinessDetailsForm";
import { EMPTY_FORM_VALUES } from "../Home/setting/business-details/BusinessDetailsForm.utils";
import { useVerifyAccountStep } from "./useVerifyAccountStep";
import { getNextVerifyStepAfterBusinessDetails } from "./verifyAccountSteps";

export default function SignUpBusinessDetailsPage() {
	const { toast } = useToast();
	const { user } = useAuth();
	const { setStep } = useVerifyAccountStep();
	const router = useRouter();
	const { location_id } = useLoaderData({ from: "__root__" });
	const statesQuery = useGetStates(US_STATES_ID);

	const mutation = useCreateGuestBusinessDetails({
		onSuccess: async () => {
			const updated = queryClient.getQueryData<UserInformation>(
				userKeys.userInformation(),
			);
			await router.invalidate();
			setStep(updated ? getNextVerifyStepAfterBusinessDetails(updated) : "pin");
		},
		onError: (error) => {
			toast({
				title: "Could not save",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	if (statesQuery.isPending) {
		return (
			<div className="flex min-h-[100dvh] items-center justify-center bg-white">
				<Spinner />
			</div>
		);
	}

	if (statesQuery.isError) {
		return (
			<div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-white px-6 text-center">
				<Typography variant="p" color="primary">
					We could not load address options. Please check your connection and
					try again.
				</Typography>
			</div>
		);
	}

	return (
		<div className="mx-auto flex min-h-[100dvh] w-full max-w-[800px] flex-col gap-4 bg-white p-6 pb-12 text-textDark">
			<SignupHeader maxProgress={55} isBackButton={false} />
			<div className="text-center">
				<h1 className="mb-2 text-2xl font-bold text-textDark">
					Add your business details
				</h1>
				<p className="text-sm font-medium text-textLight">
					This helps us set up your account and services correctly.
				</p>
			</div>
			<BusinessDetailsForm
				states={statesQuery.data}
				initialValues={EMPTY_FORM_VALUES}
				submitLabel="Save and continue"
				submitStatus={mutation.status}
				onSubmitForm={(payload) =>
					mutation.mutate({
						locationId: location_id,
						guestId: user.id,
						payload,
					})
				}
			/>
		</div>
	);
}
